import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result;
      // strip data:*/*;base64, prefix
      const idx = String(res).indexOf("base64,");
      resolve(idx >= 0 ? String(res).slice(idx + 7) : String(res));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const FIT_OPTIONS = [
  { v: "tight", l: "TIGHT", d: "BODY-CLOSE" },
  { v: "regular", l: "REGULAR", d: "TRUE TO BODY" },
  { v: "oversized", l: "OVERSIZED", d: "ROOM TO MOVE" },
];

function PhotoSlot({ label, file, preview, onPick, testid }) {
  const inputRef = useRef(null);
  return (
    <div
      data-testid={testid}
      className="flex flex-col border-2 border-[#333] bg-[#0a0a0a] p-4 transition-colors hover:border-white"
    >
      <span className="font-mono-stamp text-[10px] text-[#a0a0a0]">
        ▸ {label}
      </span>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="mt-3 flex aspect-[3/4] w-full items-center justify-center overflow-hidden border border-[#333] bg-black"
      >
        {preview ? (
          <img
            src={preview}
            alt={label}
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 px-4 text-center">
            <span className="font-display text-5xl text-[#444]">+</span>
            <span className="font-mono-stamp text-[10px] text-[#a0a0a0]">
              TAP TO UPLOAD
            </span>
            <span className="font-mono-stamp text-[9px] text-[#555]">
              JPG / PNG · MAX 8MB
            </span>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />
      <span className="mt-3 font-mono-stamp text-[10px] text-[#666]">
        {file ? `${file.name} · ${Math.round(file.size / 1024)}KB` : "NO FILE"}
      </span>
    </div>
  );
}

export default function FindYourFit() {
  const [front, setFront] = useState(null);
  const [side, setSide] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [sidePreview, setSidePreview] = useState(null);
  const [fitPref, setFitPref] = useState("regular");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (which) => (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("FILE TOO LARGE — MAX 8MB");
      return;
    }
    const url = URL.createObjectURL(file);
    if (which === "front") {
      setFront(file);
      setFrontPreview(url);
    } else {
      setSide(file);
      setSidePreview(url);
    }
  };

  const submit = async () => {
    if (!front || !side) {
      toast.error("BOTH PHOTOS REQUIRED");
      return;
    }
    setLoading(true);
    try {
      const [front_image, side_image] = await Promise.all([
        fileToBase64(front),
        fileToBase64(side),
      ]);
      const { data } = await api.post("/fit/photo", {
        front_image,
        side_image,
        fit_pref: fitPref,
      });
      setResult(data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "ANALYSIS FAILED — TRY CLEARER PHOTOS",
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setFront(null);
    setSide(null);
    setFrontPreview(null);
    setSidePreview(null);
  };

  return (
    <div data-testid="fyf-page" className="min-h-screen">
      <Navbar />

      {!result ? (
        <section className="mx-auto max-w-[1400px] px-6 pb-16 pt-24 md:px-10 md:pt-28">
          <span className="font-mono-stamp text-xs text-[#a0a0a0]">
            ▸ FIND YOUR FIT · PHOTO ANALYSIS
          </span>
          <h1 className="font-display mt-4 text-5xl leading-[0.88] md:text-8xl">
            UPLOAD.
            <br />
            <span className="text-outline">GET SIZED.</span>
          </h1>
          <p className="mt-6 max-w-2xl font-mono-stamp text-xs leading-relaxed text-[#a0a0a0] md:text-sm">
            DROP TWO PHOTOS — FRONT AND SIDE. STAND NATURALLY, ARMS RELAXED.
            OUR SYSTEM ESTIMATES YOUR BODY SHAPE AND HANDS YOU A BAS1C SIZE
            CODE PLUS ITEM-BY-ITEM RECOMMENDATIONS. PHOTOS ARE AUTO-DELETED
            AFTER 24 HOURS.
          </p>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            <PhotoSlot
              testid="photo-slot-front"
              label="FRONT VIEW"
              file={front}
              preview={frontPreview}
              onPick={pick("front")}
            />
            <PhotoSlot
              testid="photo-slot-side"
              label="SIDE VIEW"
              file={side}
              preview={sidePreview}
              onPick={pick("side")}
            />
          </div>

          {/* Fit preference */}
          <div className="mt-10">
            <span className="font-mono-stamp text-[10px] text-[#a0a0a0]">
              ▸ HOW DO YOU LIKE TO WEAR IT
            </span>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
              {FIT_OPTIONS.map((opt) => {
                const active = fitPref === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`fyf-fitpref-${opt.v}`}
                    onClick={() => setFitPref(opt.v)}
                    className={`flex flex-col items-start border-2 p-4 text-left transition-all ${
                      active
                        ? "border-white bg-white text-black"
                        : "border-[#333] bg-transparent text-white hover:border-white"
                    }`}
                  >
                    <span
                      className={`font-mono-stamp text-[10px] ${active ? "text-black/60" : "text-[#a0a0a0]"}`}
                    >
                      {active ? "▸ SELECTED" : "▸ SELECT"}
                    </span>
                    <span className="font-display mt-3 text-2xl md:text-3xl">
                      {opt.l}
                    </span>
                    <span
                      className={`mt-1 font-mono-stamp text-[10px] ${active ? "text-black/60" : "text-[#555]"}`}
                    >
                      {opt.d}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center gap-6 border-t border-[#333] pt-8 md:flex-row md:justify-between">
            <p className="font-mono-stamp text-[10px] text-[#666] md:max-w-md">
              PHOTOS ARE PROCESSED PRIVATELY AND DELETED WITHIN 24 HOURS. WE
              DO NOT IDENTIFY OR PROFILE YOU — ONLY ESTIMATE PROPORTIONS.
            </p>
            <button
              type="button"
              data-testid="fyf-submit"
              onClick={submit}
              disabled={loading || !front || !side}
              className="btn-brutal btn-brutal-solid disabled:opacity-40"
            >
              {loading ? "ANALYSING…" : "REVEAL MY SIZE →"}
            </button>
          </div>
        </section>
      ) : (
        <FitResult result={result} onReset={reset} />
      )}
    </div>
  );
}

function FitResult({ result, onReset }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("VALID EMAIL REQUIRED");
      return;
    }
    setSending(true);
    try {
      await api.post("/fit/email", { fit_id: result.id, email });
      setSent(true);
      toast.success("CHECK YOUR INBOX");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "EMAIL FAILED — TRY AGAIN LATER",
      );
    } finally {
      setSending(false);
    }
  };

  const conf = Math.round((result.confidence || 0) * 100);
  const lowConf = conf < 50;

  return (
    <section
      data-testid="fyf-result"
      className="mx-auto max-w-[1400px] px-6 pb-20 pt-24 md:px-10 md:pt-28"
    >
      <div className="grid grid-cols-12 gap-10">
        {/* Hero size reveal */}
        <div className="col-span-12 md:col-span-5">
          <span className="font-mono-stamp text-xs text-[#a0a0a0]">
            ▸ YOUR BAS1C SIZE
          </span>
          <div className="animate-stamp mt-8 inline-flex -rotate-2 flex-col items-center border-2 border-white bg-black px-10 py-8">
            <span className="font-mono-stamp text-xs text-[#a0a0a0]">
              SIZE CODE / STAMPED
            </span>
            <span
              data-testid="fyf-size-code"
              className="font-display mt-3 text-[8rem] leading-none md:text-[10rem]"
            >
              {result.size_code}
            </span>
            <span className="mt-4 font-mono-stamp text-sm text-[#a0a0a0]">
              ≈ TRADITIONAL {result.size_label}
            </span>
          </div>
          <div className="mt-6 flex items-center gap-4">
            <div className="flex-1">
              <p className="font-mono-stamp text-[10px] text-[#666]">
                ▸ AI CONFIDENCE
              </p>
              <div className="mt-2 h-[3px] w-full bg-[#222]">
                <div
                  className="h-full bg-white"
                  style={{ width: `${conf}%` }}
                />
              </div>
              <p className="mt-1 font-mono-stamp text-[10px] text-[#a0a0a0]">
                {conf}%{lowConf ? " · LOW — RETAKE WITH BETTER LIGHT?" : ""}
              </p>
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-3 md:flex-row">
            <Link
              to="/"
              data-testid="fyf-shop-cta"
              className="btn-brutal btn-brutal-solid"
            >
              SHOP THE DROP →
            </Link>
            <button
              type="button"
              data-testid="fyf-reset"
              onClick={onReset}
              className="btn-brutal"
            >
              RETAKE
            </button>
          </div>
        </div>

        {/* Body spec + per-garment recs */}
        <div className="col-span-12 md:col-span-7">
          <span className="font-mono-stamp text-[10px] text-[#a0a0a0]">
            ▸ BODY SPEC / INFERRED
          </span>
          <div className="mt-3 grid grid-cols-2 gap-px bg-[#333] md:grid-cols-3">
            {Object.entries(result.inferred || {})
              .filter(([k]) => k !== "confidence")
              .map(([k, v]) => (
                <div key={k} className="bg-[#0a0a0a] p-4">
                  <p className="font-mono-stamp text-[9px] text-[#666]">
                    {k.replace(/_/g, " ").toUpperCase()}
                  </p>
                  <p className="font-mono-stamp mt-1 text-sm text-white">
                    {String(v).toUpperCase()}
                  </p>
                </div>
              ))}
          </div>

          <span className="mt-10 block font-mono-stamp text-[10px] text-[#a0a0a0]">
            ▸ PER-GARMENT RECOMMENDATIONS
          </span>
          <div className="mt-3 border border-[#333]">
            {(result.recommendations || []).map((r) => (
              <div
                key={r.item_id}
                data-testid={`rec-${r.item_id}`}
                className="flex flex-col gap-1 border-b border-[#333] p-4 last:border-b-0 md:flex-row md:items-center md:justify-between md:gap-6"
              >
                <span className="font-display text-xl md:text-2xl">
                  {r.item_name.toUpperCase()}
                </span>
                <span className="font-mono-stamp text-[11px] text-[#a0a0a0]">
                  {r.advice}
                </span>
              </div>
            ))}
          </div>

          {/* Email me my fit */}
          <div className="mt-10 border-2 border-[#333] bg-[#0a0a0a] p-6">
            <span className="font-mono-stamp text-[10px] text-[#a0a0a0]">
              ▸ EMAIL ME MY FIT
            </span>
            <p className="mt-2 font-mono-stamp text-[11px] text-[#666]">
              GET YOUR SIZE, BODY SPEC, AND ITEM RECS DELIVERED. WE'LL ALSO
              ADD YOU TO THE LIST FOR EARLY DROP ACCESS.
            </p>
            {sent ? (
              <p
                data-testid="fyf-email-sent"
                className="mt-4 font-mono-stamp text-xs text-white"
              >
                ✓ SENT TO {email.toUpperCase()} — CHECK YOUR INBOX.
              </p>
            ) : (
              <form
                onSubmit={sendEmail}
                className="mt-4 flex flex-col gap-3 md:flex-row"
              >
                <input
                  type="email"
                  data-testid="fyf-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="YOU@EMAIL.COM"
                  className="flex-1 border-b-2 border-[#444] bg-transparent py-2 font-mono-stamp text-sm text-white placeholder:text-[#555] focus:border-white focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="fyf-email-submit"
                  disabled={sending}
                  className="btn-brutal !py-2 !text-[10px] disabled:opacity-50"
                >
                  {sending ? "SENDING…" : "EMAIL IT"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
